import React, { useCallback, useContext, useMemo } from 'react';

import {
  Grid,
  ClearIcon,
  ContentCopyIcon,
  ContentPasteIcon,
  Section,
  Actions,
  Action,
} from '@gogame/components';
import {
  FormContext,
  FormField,
  withFormFieldGroupFormField,
} from '@gogame/form';

import ChannelsFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/ChannelsField';
import ColorFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/ColorField';
import CreativePreview from 'scenes/Gallery/GalleryPage/Creatives/components/CreativePreview';
import CreativeTypeFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/CreativeTypeField';
import {
  DesignerAndOutsourceStudioProvider,
  StudioType,
  StudioComponent,
} from 'scenes/Gallery/GalleryPage/Creatives/components/DesignerAndOutsourceStudio';
import EnhancerFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/EnhancersField';
import OSFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/OSField';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/constants';
import {
  loadRequestedBy,
  loadNetworks,
  loadLanguages,
  loadApplications,
  loadTags,
  loadInheritTags,
  loadAllBusinessPartners,
  loadExclusiveForAffiliates,
  loadTested,
  loadProductionTypes,
} from 'scenes/Gallery/GalleryPage/Creatives/sources/fields';
import {
  useFileMetadata,
  useUploader,
} from 'scenes/Gallery/components/Uploader';
import {
  CREATIVE_TYPE_CINEMAGRAPH,
  CREATIVE_TYPE_VIDEO,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import TagsMultiSelect from 'scenes/GalleryTags/components/TagsMultiSelect';

import BusinessPartnersFormField from './components/BusinessPartnersFormField';
import { useCopyCreativeFields } from './CopyCreativeFields';

const Creative = withFormFieldGroupFormField({
  padding: 0,
})(({ name, creative, remove, processAsync, isUniqueYoutubeLink }) => {
  const {
    isAllowBusinessPartner,
    isAllowRequestedBy,
    isAllowJiraTask,
    isAllowDesigner,
    isAllowOutsourceStudio,
    isAllowExclusiveForAffiliates,
    isAllowAddTags,
    isAllowTested,
    isAllowProductionType,
  } = useGalleryPermissions();

  const { [CREATIVE.CREATIVE_TYPE]: creativeTypeId, [CREATIVE.FILE]: file } =
    creative;

  const fileUpload = useUploader({
    file,
    processAsync,
  });

  const fileMetadata = useFileMetadata({
    file,
  });

  const { setFieldValue } = useContext(FormContext);

  const { hasCopyBuffer, copy, paste } = useCopyCreativeFields();

  const handleCopyCreativeFields = useCallback(() => {
    const { description: omittedDescription, ...copiedCreativeProps } =
      creative;
    copy(copiedCreativeProps);
  }, [copy, creative]);

  const handlePasteCreativeFields = useCallback(() => {
    paste((copyState) => {
      setFieldValue(name, { ...creative, ...copyState });
    });
  }, [paste, setFieldValue, name, creative]);

  const handleRemove = () => {
    remove();
  };

  const isVisibleYoutubeLink = useMemo(() => {
    return [CREATIVE_TYPE_VIDEO, CREATIVE_TYPE_CINEMAGRAPH].includes(
      creativeTypeId
    );
  }, [creativeTypeId]);

  const validateYoutubeLink = (value) => {
    if (value && !isUniqueYoutubeLink(value)) {
      return 'YouTube URL must be unique';
    }
  };

  return (
    <Section
      title={'Creative'}
      subTitle={file.name}
      isCollapsable={true}
      action={
        <Actions>
          <Action
            icon={<ContentCopyIcon />}
            onClick={handleCopyCreativeFields}
            tooltip={'Copy'}
          />
          <Action
            icon={<ContentPasteIcon />}
            onClick={handlePasteCreativeFields}
            disabled={!hasCopyBuffer}
            tooltip={'Paste'}
          />
          <Action
            icon={<ClearIcon />}
            onClick={handleRemove}
            tooltip={'Remove'}
          />
        </Actions>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={3} display={'flex'}>
          <CreativePreview
            creativeType={creativeTypeId}
            fileInfo={{
              ...fileUpload,
              ...fileMetadata,
            }}
            width={'auto'}
            height={'auto'}
            maxWidth={'calc(100% - 16px)'}
            maxHeight={244}
          />
        </Grid>
        <Grid item xs={9}>
          <Grid container spacing={3}>
            <Grid item xs={2}>
              <CreativeTypeFormComponent
                label={'Creative type'}
                name={CREATIVE.CREATIVE_TYPE}
                depends={[
                  `${name}.${CREATIVE.FILE}`,
                  `${name}.${CREATIVE.NETWORKS}`,
                ]}
                required={true}
                padding={0}
              />
            </Grid>
            <Grid item xs={2}>
              <ColorFormComponent
                creativeType={creativeTypeId}
                name={CREATIVE.COLOR}
                padding={0}
              />
            </Grid>
            <Grid item xs={2}>
              <FormField.Select
                label={'Application'}
                name={CREATIVE.APPLICATION}
                source={loadApplications}
                depends={[CREATIVE.CONCEPT_ID]}
                canDeselect={true}
                padding={0}
                lazy={true}
              />
            </Grid>
            <Grid item xs={2}>
              <FormField.MultiSelect
                label={'Networks'}
                name={CREATIVE.NETWORKS}
                source={loadNetworks}
                depends={[`${name}.${CREATIVE.APPLICATION}`]}
                padding={0}
                required={true}
                lazy={true}
              />
            </Grid>
            <Grid item xs={2}>
              <ChannelsFormComponent
                name={CREATIVE.CHANNELS}
                creativeType={creativeTypeId}
                padding={0}
                lazy={true}
              />
            </Grid>
            <Grid item xs={2}>
              {isAllowExclusiveForAffiliates ? (
                <FormField.MultiSelect
                  label={'Exclusive for affiliates'}
                  name={CREATIVE.EXCLUSIVE_FOR_AFFILIATES}
                  source={loadExclusiveForAffiliates}
                  padding={0}
                />
              ) : null}
            </Grid>
            <Grid item xs={2}>
              <OSFormComponent
                name={CREATIVE.OS}
                creativeType={creativeTypeId}
                padding={0}
              />
            </Grid>
            <Grid item xs={2}>
              <FormField.Select
                label={'Language'}
                name={CREATIVE.LANGUAGE}
                source={loadLanguages}
                dropdownWidth={200}
                required={true}
                padding={0}
                lazy={true}
              />
            </Grid>
            {isAllowProductionType ? (
              <Grid item xs={2}>
                <FormField.Select
                  label={'Production type'}
                  name={CREATIVE.PRODUCTION_TYPE}
                  source={loadProductionTypes}
                  required={true}
                  padding={0}
                />
              </Grid>
            ) : null}
            <Grid item xs={2}>
              <EnhancerFormComponent
                name={CREATIVE.ENHANCERS}
                creativeType={creativeTypeId}
                padding={0}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Checkbox
                label={'AI Generated'}
                name={CREATIVE.AI_GENERATED}
                padding={0}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.MultiSelect
                label={'Inherit Internal Tags'}
                name={CREATIVE.INHERIT_TAGS}
                source={loadInheritTags}
                depends={[CREATIVE.CONCEPT_ID]}
                padding={0}
                lazy={true}
              />
            </Grid>
            <Grid item xs={4}>
              <TagsMultiSelect
                name={CREATIVE.TAGS}
                depends={[CREATIVE.CONCEPT_ID]}
                lazy={true}
                source={loadTags}
                withAddTags={isAllowAddTags}
              />
            </Grid>
            <Grid item xs={4}>
              {isAllowJiraTask ? (
                <FormField.TextField
                  label={'Jira task URL'}
                  name={CREATIVE.JIRA_TASK}
                  padding={0}
                />
              ) : null}
            </Grid>
            {isAllowTested ? (
              <Grid item xs={2}>
                <FormField.Select
                  label={'Tested'}
                  name={CREATIVE.TESTED}
                  source={loadTested}
                  required={true}
                  padding={0}
                />
              </Grid>
            ) : null}
            <Grid item xs={2}>
              {isAllowRequestedBy ? (
                <FormField.Select
                  label={'Requested by'}
                  name={CREATIVE.REQUESTED_BY}
                  source={loadRequestedBy}
                  required={true}
                  padding={0}
                  lazy={true}
                />
              ) : null}
            </Grid>
            <Grid item xs={4}>
              <FormField.TextField
                label={"Concept's name postfix"}
                name={CREATIVE.POSTFIX}
                helper={'Custom text for creative'}
                padding={0}
              />
            </Grid>
            <Grid item xs={2}>
              <FormField.TextField
                label={'Sound ID'}
                name={CREATIVE.SOUND_ID}
                type={'number'}
                thousandSeparator={false}
                padding={0}
                width={90}
              />
            </Grid>
            <DesignerAndOutsourceStudioProvider path={name}>
              <Grid item xs={2}>
                {isAllowDesigner && <StudioType />}
              </Grid>
              <Grid item xs={2}>
                {isAllowOutsourceStudio && <StudioComponent />}
              </Grid>
            </DesignerAndOutsourceStudioProvider>
            <Grid item xs={2}>
              {isAllowBusinessPartner ? (
                <BusinessPartnersFormField
                  label={'On behalf of BP'}
                  name={CREATIVE.BUSINESS_PARTNERS}
                  source={loadAllBusinessPartners}
                  padding={0}
                  lazy={true}
                />
              ) : null}
            </Grid>
            <Grid item xs={4}>
              <FormField.TextField
                label={'Description'}
                name={CREATIVE.DESCRIPTION}
                padding={0}
              />
            </Grid>
            <Grid item xs={4}>
              {isVisibleYoutubeLink ? (
                <FormField.TextField
                  label={'YouTube URL'}
                  name={CREATIVE.YOUTUBE_LINK}
                  validate={validateYoutubeLink}
                  padding={0}
                />
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Section>
  );
});

export default Creative;
